const API_ORIGIN = import.meta.env.VITE_API_URL || "";
const BASE_URL = `${API_ORIGIN}/api/v1`;
const CSRF_TOKEN_URL = `${API_ORIGIN}/api/csrf-token`;

const CSRF_COOKIE_NAME = "quiz-commerce.x-csrf-token";
const STATE_CHANGING_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

let cachedCsrfToken: string | null = null;

function getCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) return null;
  for (const cookie of document.cookie.split(";")) {
    const [cookieName, ...valueParts] = cookie.split("=");
    if (cookieName?.trim() === name) {
      const value = valueParts.join("=").trim();
      return value ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

function getCsrfToken(): string | null {
  return getCookie(CSRF_COOKIE_NAME) || cachedCsrfToken;
}

export async function fetchCsrfToken(): Promise<void> {
  try {
    const response = await fetch(CSRF_TOKEN_URL, { credentials: "include" });
    if (response.ok) {
      const data = (await response.json()) as { csrfToken: string };
      cachedCsrfToken = data.csrfToken;
    }
  } catch {
    /* silently fail — backend may not be available */
  }
}

interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

export async function apiClient<T>(
  endpoint: string,
  options: ApiOptions = {},
): Promise<T> {
  const { skipAuth = false, headers: customHeaders, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(customHeaders as Record<string, string>),
  };

  if (!skipAuth) {
    const token = localStorage.getItem("access_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // Tenant context is derived from JWT token server-side (T-14 architecture).
    // No X-Tenant-ID header — it was an IDOR vector (blocked by PR #449).
  }

  const method = (fetchOptions.method || "GET").toUpperCase();
  if (STATE_CHANGING_METHODS.includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }
  }

  let response = await fetch(`${BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
    credentials: "include",
  });

  if (response.status === 401 && !skipAuth) {
    const { refreshAccessToken } = await import("./auth");
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = localStorage.getItem("access_token");
      const retryHeaders = { ...headers };
      if (newToken) retryHeaders["Authorization"] = `Bearer ${newToken}`;
      const retryResponse = await fetch(`${BASE_URL}${endpoint}`, {
        ...fetchOptions,
        headers: retryHeaders,
        credentials: "include",
      });
      if (retryResponse.ok) {
        const retryBody = (await retryResponse.json()) as ApiResponse<T>;
        return retryBody.data;
      }
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    window.dispatchEvent(new CustomEvent("auth:expired"));
    throw new Error("Sitzung abgelaufen — bitte erneut einloggen.");
  }

  // CSRF self-heal: a 403 on a state-changing request is almost always a stale or
  // missing CSRF token (e.g. cookie lost in a long-open session). Refetch the token
  // and retry the request exactly once — mirrors the frontend client (handleCsrfError).
  if (response.status === 403 && STATE_CHANGING_METHODS.includes(method)) {
    await fetchCsrfToken();
    const refreshedCsrfToken = getCsrfToken();
    if (refreshedCsrfToken) {
      const retryHeaders = { ...headers, "X-CSRF-Token": refreshedCsrfToken };
      const retryResponse = await fetch(`${BASE_URL}${endpoint}`, {
        ...fetchOptions,
        headers: retryHeaders,
        credentials: "include",
      });
      if (retryResponse.ok) {
        const retryBody = (await retryResponse.json()) as ApiResponse<T>;
        return retryBody.data;
      }
      // Retry still failed — surface the retry response via the generic handler below.
      response = retryResponse;
    }
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    throw new Error(
      (errorBody.message as string) ||
        (errorBody.error as string) ||
        `Request fehlgeschlagen (${response.status})`,
    );
  }

  const body = (await response.json()) as ApiResponse<T>;
  return body.data;
}
